import apiClient from '../../apis';

/**
 * Connections API.
 *
 * Note what is absent: there is no field anywhere in these types that carries a
 * credential. A connection stores non-secret `coordinates` plus a `secretRef`
 * pointer (`env:NAME` / `awssm:arn:...` / `none`) that the backend resolves at
 * query time. That is design decision D1, and it is why the wizard has no
 * password input.
 */

export type ProviderStatus = 'available' | 'planned';
export type ConnectionStatus = 'untested' | 'ok' | 'failed' | 'disabled';

export interface ProviderField {
  name: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'textarea';
  required: boolean;
  placeholder: string;
  help: string;
  options: string[];
  default: any;
  advanced: boolean;
}

export interface AuthMode {
  id: string;
  label: string;
  description: string;
  requires_secret: boolean;
  secret_label: string;
}

export interface Provider {
  id: string;
  label: string;
  description: string;
  kind: 'sql' | 'mcp' | 'report' | 'timeline' | 'voice' | 'meeting' | 'knowledge' | 'email' | 'slack_member' | 'code';
  dialect: string;
  governed: boolean;
  status: ProviderStatus;
  coordinate_fields: ProviderField[];
  auth_modes: AuthMode[];
  secret_schemes: string[];
  note: string;
}

export interface Connection {
  id: string;
  owner_id: string;
  name: string;
  kind: 'sql' | 'mcp' | 'report' | 'timeline' | 'voice' | 'meeting' | 'knowledge' | 'email' | 'slack_member' | 'code';
  provider: string;
  dialect: string;
  governed: boolean;
  coordinates: Record<string, any>;
  secret_ref: string;
  read_only: boolean;
  status: ConnectionStatus;
  last_tested_at: string | null;
  last_error: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface ConnectionEvent {
  id: number;
  connection_id: string;
  kind: string;
  ok: boolean;
  detail: Record<string, any>;
  actor_user_id: string | null;
  created_at: string;
}

export interface CreateConnectionBody {
  name: string;
  provider: string;
  coordinates: Record<string, any>;
  secret_ref: string;
  read_only?: boolean;
}

export interface TestResult {
  success: boolean;
  status: ConnectionStatus;
  error?: string;
  /** A pipeline code connection (#2264): what the token can do beyond what's needed. */
  warning?: string | null;
  connection?: Connection;
}

/** Errors the API returns as `{success:false, error}` with a 4xx status. */
export class ConnectionApiError extends Error {
  status: number;
  details: Record<string, any>;

  constructor(message: string, status: number, details: Record<string, any> = {}) {
    super(message);
    this.name = 'ConnectionApiError';
    this.status = status;
    this.details = details;
  }
}

const unwrap = (e: any): never => {
  const status = e?.response?.status ?? 0;
  const data = e?.response?.data ?? {};
  throw new ConnectionApiError(data.error || e?.message || 'Request failed', status, data);
};

/** What credential flows this deployment supports. */
export interface SecretIntake {
  /** True when Voyager can accept a credential and write it to a vault. */
  vault_write: boolean;
  backend: string;
  /** Env var name prefixes the reference-only path will accept. */
  env_prefixes: string[];
}

export interface ProviderCatalogue {
  providers: Provider[];
  secretIntake: SecretIntake;
}

export const listProviders = async (): Promise<ProviderCatalogue> => {
  try {
    const { data } = await apiClient.get('/api/connections/providers');
    return {
      providers: data.providers || [],
      secretIntake: data.secret_intake || {
        vault_write: false, backend: 'none', env_prefixes: [],
      },
    };
  } catch (e) {
    return unwrap(e);
  }
};

/**
 * Hand a credential to the backend, which writes it to the deployment's secret
 * store and returns a reference.
 *
 * This is the only call in the client that carries a credential, and it is
 * deliberately not part of connection creation: the value exists in this one
 * request and nowhere else. The response contains a reference, never the value.
 */
export const storeSecret = async (label: string, value: string): Promise<string> => {
  try {
    const { data } = await apiClient.post('/api/connections/secrets', { label, value });
    return data.secret_ref;
  } catch (e) {
    return unwrap(e);
  }
};

export const listConnections = async (): Promise<Connection[]> => {
  try {
    const { data } = await apiClient.get('/api/connections');
    return data.connections || [];
  } catch (e) {
    return unwrap(e);
  }
};

export const getConnection = async (id: string): Promise<Connection> => {
  try {
    const { data } = await apiClient.get(`/api/connections/${id}`);
    return data.connection;
  } catch (e) {
    return unwrap(e);
  }
};

export const createConnection = async (body: CreateConnectionBody): Promise<Connection> => {
  try {
    const { data } = await apiClient.post('/api/connections', body);
    return data.connection;
  } catch (e) {
    return unwrap(e);
  }
};

export const updateConnection = async (
  id: string,
  updates: Partial<CreateConnectionBody> & { status?: ConnectionStatus },
): Promise<Connection> => {
  try {
    const { data } = await apiClient.patch(`/api/connections/${id}`, updates);
    return data.connection;
  } catch (e) {
    return unwrap(e);
  }
};

export const deleteConnection = async (id: string): Promise<void> => {
  try {
    await apiClient.delete(`/api/connections/${id}`);
  } catch (e) {
    unwrap(e);
  }
};

export const setDefaultConnection = async (id: string): Promise<Connection> => {
  try {
    const { data } = await apiClient.post(`/api/connections/${id}/default`);
    return data.connection;
  } catch (e) {
    return unwrap(e);
  }
};

/**
 * Test connectivity. Resolves (rather than throws) on a failed test — a
 * warehouse that refuses the connection is a result to display, not an
 * exception. Only transport-level failures reject.
 */
export const testConnection = async (id: string): Promise<TestResult> => {
  try {
    const { data } = await apiClient.post(`/api/connections/${id}/test`);
    return data;
  } catch (e: any) {
    const data = e?.response?.data;
    if (data && typeof data.error === 'string') {
      return { success: false, status: 'failed', error: data.error };
    }
    return unwrap(e);
  }
};

/** What a timeline sync (#194) answered: counts, or the feed's refusal. */
export interface SyncResult {
  success: boolean;
  status: 'synced' | 'failed' | string;
  error?: string;
  sync?: { events?: number; written?: number; updated?: number; unchanged?: number; refused?: number; runs_seen?: number; campaigns_seen?: number };
  connection?: Connection;
}

/** Read a timeline feed now (#194). Resolves on a failed sync — the feed's refusal is a result to show. */
export const syncConnection = async (id: string): Promise<SyncResult> => {
  try {
    const { data } = await apiClient.post(`/api/connections/${id}/sync`);
    return data;
  } catch (e: any) {
    const data = e?.response?.data;
    if (data && typeof data.error === 'string') {
      return { success: false, status: 'failed', error: data.error };
    }
    return unwrap(e);
  }
};

export const listConnectionEvents = async (
  id: string,
  limit = 50,
): Promise<ConnectionEvent[]> => {
  try {
    const { data } = await apiClient.get(`/api/connections/${id}/events`, {
      params: { limit },
    });
    return data.events || [];
  } catch (e) {
    return unwrap(e);
  }
};

// ---------------------------------------------------------------------------
// Cold-start introspection (#40 backend, #21 UI)
// ---------------------------------------------------------------------------

export type IntrospectionRunStatus = 'pending' | 'running' | 'completed' | 'failed';

/** Pipeline stages, in order. `done` is terminal-completed. */
export type IntrospectionStage =
  | '' | 'introspect' | 'mine' | 'profile' | 'propose' | 'draft' | 'done';

export interface DiscoveryOptions { mode?: 'legacy' | 'discovery'; requirements?: string; scope?: 'connection' | 'schema'; }

export interface IntrospectionRunResult {
  mode?: 'discovery';
  drafts?: Array<{ id: string; name: string; status: 'draft' | 'preserved'; reason?: string; metrics?: number; tables?: number }>;
  organization_reason?: string;
  /** Perspectives proposed during the run that did not land, with why. */
  withdrawn?: Array<{ name: string; reason: string }>;
  /** Model calls, tokens and cost — kept on failed runs too. */
  cost?: { calls: number; input: number; output: number; cache_write: number; cache_read: number; cost_usd: number; models: string[] };
  coverage?: Array<{ source: string; disposition: string; reason: string }>;
  collection_coverage?: Array<{ collection: string; status: string; error?: string }>;
  warnings?: string[];
  tables?: number;
  columns?: number;
  history?: { available: boolean; note: string };
  signal?: {
    jobs_scanned: number;
    jobs_relevant: number;
    jobs_failed: number;
    top_tables: Array<[string, number]>;
  } | null;
  hub_table?: string;
  proposal?: {
    metrics: number;
    dimensions: number;
    relationships: number;
    warnings: string[];
    llm_used: boolean;
  };
  draft_id?: string | null;
  draft_updated?: boolean;
}

export interface IntrospectionRun {
  id: string;
  connection_id: string;
  status: IntrospectionRunStatus;
  stage: IntrospectionStage;
  error: string;
  result: IntrospectionRunResult;
  draft_id: string | null;
  table_count: number;
  column_count: number;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

/**
 * Kick off an async cold-start run. Returns the pending run row immediately;
 * progress comes from polling getIntrospectionRun.
 */
export const startIntrospection = async (id: string, options: DiscoveryOptions = {}): Promise<IntrospectionRun> => {
  try {
    const { data } = await apiClient.post(`/api/connections/${id}/introspect`, options);
    return data.run;
  } catch (e) {
    return unwrap(e);
  }
};

export const getIntrospectionRun = async (
  connectionId: string,
  runId: string,
): Promise<IntrospectionRun> => {
  try {
    const { data } = await apiClient.get(
      `/api/connections/${connectionId}/introspect/runs/${runId}`,
    );
    return data.run;
  } catch (e) {
    return unwrap(e);
  }
};

export const listIntrospectionRuns = async (
  connectionId: string,
  limit = 20,
): Promise<IntrospectionRun[]> => {
  try {
    const { data } = await apiClient.get(
      `/api/connections/${connectionId}/introspect/runs`,
      { params: { limit } },
    );
    return data.runs || [];
  } catch (e) {
    return unwrap(e);
  }
};

/** Where the workspace's decisions are used (#567): counts and configuration only, never who. */
export interface UsedIn {
  /** null without the database */
  slack: { people: number; channels: number } | null;
  extension: { browsers: number; people: number } | null;
  tableau: number;
  /** `oauth`: how many OAuth client applications may call as a user, or "any" under dynamic registration. */
  mcp: {
    url: string | null; oauth: number | 'any';
    /** How the MCP server itself lets callers in (its /healthz); null when this deployment does not say. */
    server?: 'oauth_any' | 'oauth_client' | 'api_key' | 'unconfigured' | 'unavailable' | null;
  };
  /** Agents checking actions over MCP (#575), last `window_days`: counts only; null when unreadable. */
  agents?: { callers: number; checks: number; objectives: number; window_days: number } | null;
}

export const getUsedIn = async (): Promise<UsedIn> => {
  try {
    // the client's interceptor has already unwrapped the { success, data } envelope
    const { data } = await apiClient.get('/api/connections/used-in');
    return data;
  } catch (e) {
    return unwrap(e);
  }
};
